import events from "@/events/events"
import Group from "@/types/Group.interface"
import Pagination from "@/types/Pagination.interface"
import Person from "@/types/Person.interface"

interface State {
    groups: Group[]
    groupsBuckets: Group[]
    groupsLoading: boolean
    groupsRecent: Group[]
    groupSelectorGroups: Group[]
    optOutGroup: Group
}

const defaults = () => {
    return {
        groups: [],
        groupsBuckets: [],
        groupsLoading: false,
        groupsRecent: [],
        groupSelectorGroups: [],
        optOutGroup: null
    }
}
export default {
    state: defaults(),
    mutations: {
        groupsBucketsSet (state: State, groups: Group[]): void {
            state.groupsBuckets = groups
        },
        groupsBucketsPush (state: State, groups: Group[]): void {
            //  Filter out any non bucket groups
            const filtered = groups.filter((x: Group) => x.bucket || x.name === 'All Contacts')

            //  Update with the filtered
            state.groupsBuckets = window.removeDuplicatesById(state.groupsBuckets, filtered)
        },
        groupsBucketsRemove (state: State, ids: number[]): void {
         //  Get our current lists of groups
         const groups = state.groupsBuckets

         //  If no groups, do nothing
         if (!groups) return

         //  Else, filter out our removed ids
         const filtered = groups.filter((x: Group) => !ids.includes(x.id))

         //  Set our groups to the filtered list
         state.groupsBuckets = filtered   
        },
        groupsRecentPush (state: State, groups: Group[]): void {
            state.groupsRecent = window.removeDuplicatesById(state.groupsRecent, groups)
        },
        groupSelectorGroupsPush (state: State, groups: Group[]): void {
            state.groupSelectorGroups = window.removeDuplicatesById(state.groupSelectorGroups, groups)
        },
        groupSelectorGroupsRemove (state: State, ids: number[]): void {
         //  Get our current lists of groups
         const groups = state.groupSelectorGroups

         //  If no groups, do nothing
         if (!groups) return

         //  Else, filter out our removed ids
         const filtered = groups.filter((x: Group) => !ids.includes(x.id))

         //  Set our groups to the filtered list
         state.groupSelectorGroups = filtered   
        },
        groupSelectorGroupsSet (state: State, groups: Group[]): void {
            state.groupSelectorGroups = groups
        },
        groupsPush (state: State, groups: Group[]): void {
            state.groups = window.removeDuplicatesById(state.groups, groups)
        },
        groupsRemove (state: State, ids: number[]): void {
            //  Get our current lists of groups
            const groups = state.groups

            //  If no groups, do nothing
            if (!groups) return

            //  Else, filter out our removed ids
            const filtered = groups.filter((x: Group) => !ids.includes(x.id))

            //  Set our groups to the filtered list
            state.groups = filtered
        },
        groupsSet (state: State, groups: Group[]): void {
            state.groups = groups
        },
        groupsLoadingSet (state: State, bool: boolean): void {
            state.groupsLoading = bool
        },
        optOutGroupSet (state: State, group: Group): void {
            state.optOutGroup = group
        },
        groupsReset (state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        //  Gets our global groups dataset
        groupsGet({ commit, getters }): Promise<Group[]> {
            //  Assemble our API endpoint
            const url = `${getters.api}/groups/all`

            //  Start a loading tracker
            commit('groupsLoadingSet', true)

            //  Send the request
            return window.axios.get(url).then(response => {
                //  NOTE: We do NOT set the global groups list here because they do NOT have persons
                //  Instead, we actually uncache all of our groups
                commit('groupsSet', [])

                //  Set our group selector groups
                commit('groupSelectorGroupsSet', response.data)

                //  Set our lists groups
                commit('listsGroupsSet', response.data)

                //  Set our broadcasts groups
                commit('broadcastsViewGroupsSet', response.data)

                //  Set our person groups
                commit('personGroupsSet', response.data)

                //  Stop the loading tracker
                commit('groupsLoadingSet', false)

                //  Return our groups
                return response.data
            }).catch(() => null)
        },
        //  Whenever we get bucket groups, they have an unread field used for count so we need to reach out to API everytime
        groupsBucket({ commit, getters }): Promise<Group[]> {
            //  Assemble our API endpoint
            const url = `${getters.api}/groups/bucket`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our bucket groups
                commit('groupsBucketsSet', response.data)

                /** We do NOT add to global dataset here for 2 reasons: 
                 *  1. Groups ALWAYS has a full dataset so we don't need to update it with this 1 call
                 *  2. Bucket Groups have additional traits that regular groups don't, avoids confusion
                 * */  

                //  Return our bucket groups
                return response.data
            }).catch(() => null)
        },
        groupsCsvExport ({ getters }, groupId: number): Promise<string[]> {
            //  Assemble our API endpoint
            const url = `${getters.api}/groups/export/${groupId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Just return our response from the server
                return response.data
            }).catch(() => null)
        },
        groupsDelete({ commit, getters }, group: Group): Promise<Group> {
            //  Grab our id from the supplied group
            const id = group.id

            //  Assemble the API endpoint
            const url = `${getters.api}/groups/${id}`

            //  Send the request
            return window.axios.delete(url).then(response => {
                //  Remove this group from our global dataset
                commit('groupsRemove', [response.data.id])

                //  Remove this group from our bucket groups
                commit('groupsBucketsRemove', [response.data.id])

                //  Update our group selector list
                commit('groupSelectorGroupsRemove', [response.data.id])

                //  Remove this group from our lists groups
                commit('listsGroupsRemove', [response.data.id])

                //  Remove this group from our broadcasts groups
                commit('broadcastsViewGroupsRemove', [response.data.id])

                //  Remove this group from our person group
                commit('personGroupsRemove', [response.data.id])

                //  Return our deleted group
                return response.data
            }).catch(() => null)
        },
        groupsDeleteBatch({ commit, getters }, ids: number[]): Promise<number[]> {
            //  Construct our payload
            const payload = { data: {ids: ids} }

            //  Assemble the API endpoint
            const url = `${getters.api}/groups/batch`

            //  Send the request
            return window.axios.delete(url, payload).then(response => {
                //  Remove this group from our global dataset
                commit('groupsRemove', response.data)

                //  Remove this group from our bucket groups
                commit('groupsBucketsRemove', response.data)

                //  Remove from group selector list
                commit('groupSelectorGroupsRemove', response.data)

                //  Remove this group from our lists groups
                commit('listsGroupsRemove', response.data)

                //  Remove this group from our broadcasts groups
                commit('broadcastsViewGroupsRemove', response.data)

                //  Remove this group from our person group
                commit('personGroupsRemove', response.data)

                //  Return the ids of the deleted groups
                return response.data
            }).catch(() => null)
        },
        groupsExceptions ({ getters }, groupId: number): Promise<Person[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/groups/${groupId}/exceptions`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return the exceptions
                return response.data
            }).catch(() => null)
        },
        groupsFind ({ getters, commit }, groupId: number): Promise<Group> {
            //  Grab our global groups
            const groups = getters.groups

            //  Try to find this group in that dataset
            const existingGroup = groups.find((x: Group) => x.id == groupId)

            //  If existing group is found, return them
            if (existingGroup) return existingGroup

            //  Assemble the API endpoint
            const url = `${getters.api}/groups/${groupId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return our group
                return response.data
            }).catch(() => null)
        },
        groupsOptOut ({ getters, commit }): Promise<Group> {
            //  Check if this group is set
            const existing = getters.optOutGroup

            //  If it does, return it
            if (existing) return existing

            //  Assemble the API endpoint
            const url = `${getters.api}/groups/opt`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our opt out group
                commit('optOutGroupSet', response.data)

                //  Return our group
                return response.data
            })
        },
        groupsPost ({ commit, getters }, group: Group): Promise<Group> {
            //  Assemble the API endpoint
            const url = `${getters.api}/groups`

            //  Send the request
            return window.axios.post(url, group).then(response => {
                //  Add the group to our global dataset
                commit('groupsPush', [response.data])

                //  Update our group selector groups
                commit('groupSelectorGroupsPush', [response.data])

                //  Add it to our lists dataset
                commit('listsGroupsPush', [response.data])

                //  Add it to our broadcasts dataset
                commit('broadcastsViewGroupsPush', [response.data])

                //  Add it to our person groups
                commit('personGroupsPush', [response.data])

                //  Return our newly created group
                return response.data
            }).catch(() => null)
        },
        groupsPut ({ commit, getters }, group: Group): Promise<Group> {
            //  Assemble the API endpoint
            const url = `${getters.api}/groups`

            //  Send the request
            return window.axios.put(url, group).then(response => {
                //  Update the global dataset
                commit('groupsPush', [response.data])

                //  Update our group selector groups
                commit('groupSelectorGroupsPush', [response.data])

                //  Update our lists
                commit('listsGroupsPush', [response.data])

                //  Update our broadcasts groups
                commit('broadcastsViewGroupsPush', [response.data])

                //  Add it to our person groups
                commit('personGroupsPush', [response.data])

                //  Return our newly updated group
                return response.data
            }).catch(() => null)
        },
        groupsPutBucket ({ commit, getters }, payload: Group): Promise<Group> {
            //  Assemble the API endpoint
            const url = `${getters.api}/groups/bucket`

            //  Send the request
            return window.axios.put(url, payload).then(response => {
                //  Return our updated bucket group
                return response.data
            }).catch(() => null)
        },
        groupsPutBucketChannels({ commit, getters }, payload: Group): Promise<Group[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/groups/bucketChannels`

            //  Send the request
            return window.axios.put(url, payload).then(response => {
                //  Update our group buckets
                commit('groupsBucketsPush', response.data)

                //  Return our updated bucket groups
                return response.data
            }).catch(() => null)
        },
        groupsRecentPaginate({commit, getters}): Promise<Pagination> {
            //  Assemble our endpoint
            const url = `${getters.api}/groups/recent`

            //  Send our request
            return window.axios.get(url).then(response => {
                //  Set this in our store
                commit('groupsRecentPush', response.data)

                return response.data.data
            }).catch(() => null)
        },
        groupsWhereIn ({ getters }, ids: number[]): Promise<Group[]> {
            //  Create the payload
            const payload = { group_ids: ids }

            //  Assemble the API endpoint
            const url = `${getters.api}/groups/wherein`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Return our found groups
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        allContactsGroup (state: State): Group {
            //  Get the selector groups
            const groups = state.groupSelectorGroups

            //  If none, return nothing
            if (!groups) return

            //  Find the All Contacts Group
            const allContactsGroup = groups.find(x => x.name === 'All Contacts')

            //  If not found, return nothing
            if (!allContactsGroup) return

            //  Return the all contacts group
            return allContactsGroup
        },
        defaultBucketUnreadCount(state: State): number {
            //  Get our bucket groups
            const buckets = state.groupsBuckets

            //  If no buckets, return 0
            if (!buckets) return 0

            //  Else, grab our default group
            const defaultGroup = buckets.find(x => x.name === 'All Contacts')

            //  If no default group, return 0
            if (!defaultGroup) return 0

            //  Else, return the unread count
            return defaultGroup.unread_count
        },
        groups (state: State): Group[] {
            return state.groups
        },
        groupsBuckets (state: State): Group[] {
            return state.groupsBuckets
        },
        groupsLoading (state: State): boolean {
            return state.groupsLoading
        },
        groupsRecent (state: State): Group[] {
            return state.groupsRecent
        },
        groupSelectorGroups(state: State): Group[] {
            return state.groupSelectorGroups
        },
        usersGroup (state: State): Group {
            //  Get our groups
            const groups = state.groupSelectorGroups

            //  If no groups, return
            if (!groups) return

            //  Else, grab our users group
            const usersGroup = groups.find(x => x.name === 'All MessageDesk Users')

            //  If no users group is found, return
            if (!usersGroup) return

            //  Else, return the users group
            return usersGroup
        },
        optOutGroup(state: State): Group {
            return state.optOutGroup
        }
    }
}
